import React, { useState } from "react";
import { LOCATION_EFFECTS, processLocationReward } from "../locationEffects";
import { ExplorationLocation, Warband, ModelTemplate, ItemTemplate } from "../types";

interface LocationEffectButtonsProps {
  location: ExplorationLocation;
  onClaimReward: (locationId: string, choice?: string) => void;
  warband: Warband;
  modelTemplates: ModelTemplate[];
  itemTemplates: ItemTemplate[];
}

export const LocationEffectButtons = ({
  location,
  onClaimReward,
  warband,
  modelTemplates,
  itemTemplates
}: LocationEffectButtonsProps) => {
  const effect = LOCATION_EFFECTS[location.name];

  if (!effect) {
    return (
      <div className="text-red-400 text-sm">
        Reward system not implemented for this location
      </div>
    );
  }

  // Handle locations with multiple choices
  if (effect.choices) {
    return (
      <div className="space-y-2">
        <div className="text-yellow-400 text-sm font-semibold">
          Choose Your Reward:
        </div>
        {Object.entries(effect.choices).map(([choiceKey, choiceData]) => {
          // Calculate reward preview
          const rewardPreview = calculateRewardPreview(
            location.name,
            choiceKey,
            warband,
            { modelTemplates, itemTemplates }
          );

          // Check if choice is available
          const isAvailable = checkChoiceAvailability(choiceKey, choiceData, warband);

          // Detect if it's a simple ducats/glory reward or not
          const isSimpleReward = rewardPreview.some(preview => preview.includes('ducats') || preview.includes('glory'));

          return (
            <SimpleEffectButton
              key={choiceKey}
              isSimpleReward={isSimpleReward}
              choiceKey={choiceKey}
              choiceData={choiceData}
              rewardPreview={rewardPreview}
              isAvailable={isAvailable}
              onClick={() => onClaimReward(location.id, choiceKey)}
              locationName={location.name}
            />
          );
        })}
      </div>
    );
  }

  // Handle locations with single default action
  if (effect.defaultAction) {
    const rewardPreview = calculateRewardPreview(
      location.name,
      undefined,
      warband,
      { modelTemplates, itemTemplates }
    );

    return (
      <div className="space-y-2">
        <div className="text-yellow-400 text-sm font-semibold">
          Available Reward:
        </div>
        <button
          onClick={() => onClaimReward(location.id)}
          className="w-full bg-yellow-900 hover:bg-yellow-800 text-white px-4 py-2 rounded text-sm font-semibold border border-yellow-800 uppercase tracking-wider"
        >
          <div className="flex justify-between items-center">
            <span>Claim Reward</span>
            <span className="text-yellow-200 text-xs">
              +{rewardPreview.join(', ')}
            </span>
          </div>
        </button>
      </div>
    );
  }

  return null;
};

// Component for individual effect buttons
interface SimpleEffectButtonProps {
  isSimpleReward: boolean;
  choiceKey: string;
  choiceData: any;
  rewardPreview: string[];
  isAvailable: boolean;
  onClick: () => void;
  locationName: string;
}

const SimpleEffectButton = ({
  isSimpleReward,
  choiceKey,
  choiceData,
  rewardPreview,
  isAvailable,
  onClick,
  locationName
}: SimpleEffectButtonProps) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={isAvailable ? onClick : undefined}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={!isAvailable}
        className={`w-full px-4 py-2 rounded text-sm font-semibold border uppercase tracking-wider text-left transition-colors ${
          isAvailable
            ? (isSimpleReward
                ? 'bg-yellow-900 hover:bg-yellow-800 text-white border-yellow-800 cursor-pointer'
                : 'bg-gray-800 hover:bg-gray-700 text-white border-gray-700 cursor-pointer')
            : 'bg-gray-700 text-gray-500 border-gray-600 cursor-not-allowed'
        }`}
      >
        <div className="flex justify-between items-center">
          <span>{choiceData.description}</span>
          <div className="flex items-center gap-2">
            <span className="text-yellow-200 text-xs">
              +{rewardPreview.join(', ')}
            </span>
            {!isSimpleReward && (
              <span className="text-xs ml-2">(manual)</span>
            )}
            {!isAvailable && (
              <span className="text-red-400 text-xs">
                (Unavailable)
              </span>
            )}
          </div>
        </div>
        {!isSimpleReward && <div className="text-[11px] text-yellow-400 pt-1">Please resolve this reward manually.</div>}
      </button>

      {/* Tooltip for unavailable choices */}
      {showTooltip && !isAvailable && (
        <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg border border-gray-600 z-10 max-w-xs">
          {getUnavailableReason(choiceKey, choiceData, locationName)}
        </div>
      )}
    </div>
  );
};

// Calculate what rewards a choice will give
function calculateRewardPreview(
  locationName: string,
  choice?: string,
  warband?: any,
  templates?: any
): string[] {
  const reward = processLocationReward(locationName, choice, warband, templates);
  const preview: string[] = [];

  if (reward.ducats) preview.push(`${reward.ducats} ducats`);
  if (reward.glory) preview.push(`${reward.glory} glory`);
  if (reward.itemTemplateIds?.length) preview.push('equipment');
  if (reward.items?.length) preview.push('equipment');
  if (reward.addToWarchest && (reward.itemTemplateIds?.length || reward.items?.length)) preview.push('warchest');
  if (reward.skills?.length) preview.push('skills');
  if (reward.newModelTemplateId) preview.push('model');
  if (reward.addKeywords?.length) preview.push('keyword');

  return preview.length > 0 ? preview : ['special effect'];
}

// Check if a choice is available to the current warband
function checkChoiceAvailability(choiceKey: string, choiceData: any, warband: Warband): boolean {
  // Check faction restrictions
  if (choiceData.requirements?.faction) {
    const warbandFaction = detectWarbandFaction(warband);
    if (!choiceData.requirements.faction.includes(warbandFaction)) {
      return false;
    }
  }

  // Add other availability checks here
  return true;
}

// Get reason why a choice is unavailable
function getUnavailableReason(choiceKey: string, choiceData: any, locationName: string): string {
  if (choiceData.requirements?.faction) {
    const requiredFactions = choiceData.requirements.faction.join(', ');
    return `This option is only available to: ${requiredFactions}`;
  }

  return "This option is not available to your warband.";
}

// Helper function to detect warband faction
function detectWarbandFaction(warband: Warband): string {
  // For now, default to New Antioch - this can be enhanced later
  // to detect faction based on models or explicit faction field
  return "New Antioch";
}
